/**
 * Memories feed: within each `eventId` group, place one hero image first (if any),
 * then remaining items by `createdAt` descending. Groups ordered by their newest item.
 *
 * Future: inter-group hero “section break” rows can be inserted between blocks here
 * without changing thumbnail grid styling (keep inserts out of dense grids).
 */

export type MemoriesHeroOrderableItem = {
  id: string
  eventId: string
  type: "image" | "video"
  createdAt: Date
  isHeroCandidate?: boolean
}

export function orderMemoriesGalleryWithHeroClusters<T extends MemoriesHeroOrderableItem>(
  items: T[],
): T[] {
  if (items.length <= 1) return items

  const byEvent = new Map<string, T[]>()
  for (const it of items) {
    const eid = it.eventId.trim() || it.eventId
    const arr = byEvent.get(eid) ?? []
    arr.push(it)
    byEvent.set(eid, arr)
  }

  const blocks: T[][] = []
  for (const [, arr] of byEvent) {
    const heroCandidates = arr.filter(
      (i) => i.type === "image" && i.isHeroCandidate === true,
    )
    let hero: T | undefined
    if (heroCandidates.length > 0) {
      hero = heroCandidates.reduce((a, b) =>
        a.createdAt.getTime() >= b.createdAt.getTime() ? a : b,
      )
    }
    const rest = hero
      ? arr.filter((i) => i.id !== hero!.id)
      : [...arr]
    rest.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    blocks.push(hero ? [hero, ...rest] : rest)
  }

  blocks.sort((ga, gb) => {
    const ta = Math.max(...ga.map((x) => x.createdAt.getTime()), 0)
    const tb = Math.max(...gb.map((x) => x.createdAt.getTime()), 0)
    return tb - ta
  })

  return blocks.flat()
}
